import type { ReactNode } from 'react';
import { nip19 } from 'nostr-tools';
import { Link, useParams } from 'react-router-dom';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BadgeCheck } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useSeoMeta } from '@unhead/react';

import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { truncateMiddle } from '@/lib/owner';
import { siteConfig } from '@/config/site';
import { NoteCard } from '@/components/NoteCard';
import { FollowButton } from '@/components/FollowButton';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import NotFound from './NotFound';

function useEventById(id: string | undefined) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['note', id],
    queryFn: async ({ signal }) => {
      const [event] = await nostr.query([{ ids: [id!] }], {
        signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]),
      });
      return event ?? null;
    },
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });
}

function BackHome() {
  return (
    <p className="mt-8 text-center">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 font-mono text-sm text-muted-foreground transition-colors hover:text-primary"
      >
        <ArrowLeft className="size-4" />
        back to {siteConfig.name}'s site
      </Link>
    </p>
  );
}

function ProfileView({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = getDisplayName(metadata, pubkey);
  const npub = nip19.npubEncode(pubkey);
  const picture = sanitizeUrl(metadata?.picture);
  const banner = sanitizeUrl(metadata?.banner);

  return (
    <div className="mx-auto max-w-xl">
      <Card className="overflow-hidden">
        <div className="h-28 bg-gradient-to-r from-primary/40 via-amber-500/30 to-spark/30">
          {banner && <img src={banner} alt="" className="size-full object-cover" />}
        </div>
        <CardContent className="relative space-y-4 p-6">
          <Avatar className="-mt-16 size-20 border-4 border-card">
            <AvatarImage src={picture} alt={name} />
            <AvatarFallback className="text-xl uppercase">{name.slice(0, 2)}</AvatarFallback>
          </Avatar>

          <div className="space-y-1">
            <h1 className="flex items-center gap-1.5 font-display text-2xl font-bold">
              {name}
              {metadata?.nip05 && <BadgeCheck className="size-5 text-primary" />}
            </h1>
            {metadata?.nip05 && (
              <p className="font-mono text-sm text-primary">{metadata.nip05}</p>
            )}
            <p className="font-mono text-xs text-muted-foreground">{truncateMiddle(npub, 16, 8)}</p>
          </div>

          {metadata?.about && (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
              {metadata.about}
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <FollowButton pubkey={pubkey} />
            <Button variant="outline" asChild>
              <a
                href={`https://njump.me/${npub}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in njump
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
      <BackHome />
    </div>
  );
}

function NoteView({ id }: { id: string }) {
  const { data: event, isLoading } = useEventById(id);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-xl">
        <Card>
          <CardContent className="space-y-3 p-5">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="mx-auto max-w-xl">
        <Card className="border-dashed">
          <CardContent className="px-8 py-12 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              Note not found on the current relays. It may live on relays this site
              isn't connected to.
            </p>
          </CardContent>
        </Card>
        <BackHome />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl">
      <NoteCard event={event} />
      <BackHome />
    </div>
  );
}

function AddressableView({ event }: { event: NostrEvent }) {
  const title = event.tags.find(([name]) => name === 'title')?.[1];

  return (
    <div className="mx-auto max-w-2xl">
      <Card>
        <CardContent className="space-y-4 p-6 md:p-8">
          {title && <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>}
          <NoteCard event={event} className="border-0 bg-transparent p-0 hover:border-0" />
        </CardContent>
      </Card>
      <BackHome />
    </div>
  );
}

function AddressableLoader({
  kind,
  pubkey,
  identifier,
}: {
  kind: number;
  pubkey: string;
  identifier: string;
}) {
  const { nostr } = useNostr();
  const { data: event, isLoading } = useQuery({
    queryKey: ['addr', kind, pubkey, identifier],
    queryFn: async ({ signal }) => {
      const [found] = await nostr.query(
        [{ kinds: [kind], authors: [pubkey], '#d': [identifier], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]),
        },
      );
      return found ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl">
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="mx-auto max-w-xl">
        <Card className="border-dashed">
          <CardContent className="px-8 py-12 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              Couldn't find this event on the current relays.
            </p>
          </CardContent>
        </Card>
        <BackHome />
      </div>
    );
  }

  return <AddressableView event={event} />;
}

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  useSeoMeta({
    title: `${siteConfig.name} — shared from Nostr`,
    description: siteConfig.tagline,
  });

  let content: ReactNode;

  if (!identifier) {
    return <NotFound />;
  }

  try {
    const decoded = nip19.decode(identifier);

    switch (decoded.type) {
      case 'npub':
        content = <ProfileView pubkey={decoded.data} />;
        break;
      case 'nprofile':
        content = <ProfileView pubkey={decoded.data.pubkey} />;
        break;
      case 'note':
        content = <NoteView id={decoded.data} />;
        break;
      case 'nevent':
        content = <NoteView id={decoded.data.id} />;
        break;
      case 'naddr':
        content = (
          <AddressableLoader
            kind={decoded.data.kind}
            pubkey={decoded.data.pubkey}
            identifier={decoded.data.identifier}
          />
        );
        break;
      default:
        return <NotFound />;
    }
  } catch {
    return <NotFound />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="container flex-1 py-12 md:py-16">{content}</main>
      <SiteFooter />
    </div>
  );
}
